import { CompanySettingValue } from '@/api/companies/api';
import { CompanySettingListItem } from '@/types/company-settings';
import { Attachment } from '@/library/types/files';
import { getSettingFileId } from './helpers';

export type SettingOptionPrimitive = string | number | boolean;

export type SettingLabelValueOption<T extends SettingOptionPrimitive = SettingOptionPrimitive> = {
  label: string;
  value: T;
};

export type SettingKeyLabelItem = {
  key: string;
  label: string;
};

export type SettingNamedItem = {
  name?: string;
};

export type SettingJobStatusProperties = {
  progress?: string;
  edit_side?: string;
};

export type CompanySettingComparableValue =
  | CompanySettingValue
  | SettingOptionPrimitive
  | SettingLabelValueOption
  | SettingJobStatusProperties
  | Attachment
  | Attachment[];

type NormalizedSettingValue =
  | string
  | number
  | boolean
  | null
  | CompanySettingListItem[]
  | number[]
  | SettingJobStatusProperties;

const TRUTHY_OPTION_VALUES: SettingOptionPrimitive[] = [1, '1', true, 'true', 'enabled'];

const isEmptyValue = (
  value: CompanySettingComparableValue | undefined
): value is null | undefined | '' | [] => {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
};

const isLabelValueOption = (
  value: CompanySettingComparableValue
): value is SettingLabelValueOption => {
  return typeof value === 'object' && value !== null && 'value' in value && !Array.isArray(value);
};

const isFileishValue = (value: CompanySettingComparableValue): boolean => {
  if (Array.isArray(value)) {
    return value.some(
      item => typeof item === 'number' || (item && typeof item === 'object' && 'id' in item)
    );
  }

  return typeof value === 'object' && value !== null && 'id' in value && !('value' in value);
};

const normalizeFileIds = (value: CompanySettingComparableValue): number[] | null => {
  if (Array.isArray(value)) {
    const ids = value.map(item => getSettingFileId(item)).filter((id): id is number => id != null);
    return ids;
  }

  const id = getSettingFileId(value);
  return id == null ? null : [id];
};

const normalizeForCompare = (
  value: CompanySettingComparableValue | undefined
): NormalizedSettingValue => {
  if (isEmptyValue(value)) return null;

  if (isLabelValueOption(value)) {
    return normalizeForCompare(value.value);
  }

  if (isFileishValue(value)) {
    return normalizeFileIds(value);
  }

  return value as NormalizedSettingValue;
};

export const valuesEqual = (
  left: CompanySettingComparableValue | undefined,
  right: CompanySettingComparableValue | undefined
): boolean => {
  const normalizedLeft = normalizeForCompare(left);
  const normalizedRight = normalizeForCompare(right);

  if (normalizedLeft === normalizedRight) return true;
  if (normalizedLeft == null && normalizedRight == null) return true;
  if (normalizedLeft == null || normalizedRight == null) return false;

  if (typeof normalizedLeft === 'object' || typeof normalizedRight === 'object') {
    try {
      return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
    } catch {
      return false;
    }
  }

  return String(normalizedLeft) === String(normalizedRight);
};

const BOOLEAN_DISPLAY: Record<string, string> = {
  true: 'YES',
  false: 'NO',
  enabled: 'Enabled',
  disabled: 'Disabled'
};

const hasLabel = (value: object): value is { label: string } =>
  'label' in value && value.label != null;

const hasName = (value: object): value is { name: string } =>
  'name' in value && Boolean(value.name);

const isJobStatusProperties = (value: object): value is SettingJobStatusProperties =>
  'progress' in value || 'edit_side' in value;

export const formatDisplayValue = (value: CompanySettingComparableValue | undefined): string => {
  if (isEmptyValue(value)) return '—';

  if (typeof value === 'boolean') return value ? 'YES' : 'NO';

  if (typeof value === 'object') {
    if (hasLabel(value)) {
      return String(value.label);
    }

    if (Array.isArray(value)) {
      if (!value.length) return '—';

      if (
        value.every(item => item && typeof item === 'object' && ('label' in item || 'key' in item))
      ) {
        return value
          .map(item => {
            const entry = item as SettingKeyLabelItem;
            return entry.label && entry.key
              ? `${entry.label} (${entry.key})`
              : entry.label || entry.key || '';
          })
          .filter(Boolean)
          .join(', ');
      }

      if (value[0] && typeof value[0] === 'object' && 'name' in value[0]) {
        return (
          (value as SettingNamedItem[])
            .map(item => item.name)
            .filter(Boolean)
            .join(', ') || 'Uploaded'
        );
      }

      return 'Set';
    }

    if (hasName(value)) {
      return String(value.name);
    }

    if (isJobStatusProperties(value)) {
      return [value.progress, value.edit_side].filter(Boolean).join(' / ') || 'Set';
    }

    try {
      return JSON.stringify(value);
    } catch {
      return 'Set';
    }
  }

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return BOOLEAN_DISPLAY[normalized] ?? value;
  }

  return String(value);
};

export const formatBooleanDisplay = (
  value: CompanySettingComparableValue | undefined,
  truthyLabel = 'YES',
  falsyLabel = 'NO'
) => {
  if (isEmptyValue(value)) return '—';

  const isTruthy =
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true' ||
    value === 'enabled' ||
    (isLabelValueOption(value) && TRUTHY_OPTION_VALUES.includes(value.value));

  return isTruthy ? truthyLabel : falsyLabel;
};

export const formatOptionDisplay = <T extends SettingOptionPrimitive = SettingOptionPrimitive>(
  value: CompanySettingComparableValue | undefined,
  options?: SettingLabelValueOption<T>[]
): string => {
  if (typeof value === 'object' && value !== null && hasLabel(value)) {
    return String(value.label);
  }

  if (options) {
    const match = options.find(option => valuesEqual(option.value, value));
    if (match) return match.label;
  }

  return formatDisplayValue(value);
};
